import { Button, Menu, MenuButton, MenuItem, MenuList, Stack, Text } from '@chakra-ui/react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import type { UserDetailsFormData } from '@/client/components/learners/profile/overlay/LearnerProfileUserDetailsFormModal';
import { getSupportedLanguages } from '@/client/utils/getSupportedLanguages';
import { useAuthStore } from '@/client/services/state/authStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function LearnerProfileUserLanguageForm() {
  const { t, i18n } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;
  const { available_languages: availableLanguages } = company;
  const supportedLanguages = getSupportedLanguages();

  const sanitizeLanguage = (lang: string) => {
    if (lang === 'eng') {
      return 'en';
    }
    if (lang === 'spa') {
      return 'es_419';
    }
    return lang;
  };

  const companySupportedLanguages =
    availableLanguages
      ?.map((lang) => {
        const sanitizedLang = sanitizeLanguage(lang);
        return supportedLanguages.find((l) => l.value === sanitizedLang);
      })
      .filter((lang): lang is NonNullable<typeof lang> => lang !== undefined) || [];

  const { watch, setValue } = useFormContext<UserDetailsFormData>();

  const { language } = watch();

  const sanitizedLanguage = sanitizeLanguage(language);

  const selectedLanguage =
    supportedLanguages.find((lang) => lang.value === sanitizedLanguage) || supportedLanguages[0];

  const handleLanguageChange = (lang: string) => {
    setValue('language', lang, { shouldDirty: true, shouldValidate: true });
    i18n.changeLanguage(lang);
  };

  return (
    <Stack spacing={6}>
      <Text variant="formSectionHeading">{t('learners.profile.userLanguage.title')}</Text>

      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon color="baseBlack" />}
          backgroundColor="white"
          color="baseBlack"
          fontSize="14px"
          fontWeight={500}
          paddingY="14px"
          paddingX={3}
          border="1px solid"
          borderColor="warmNeutral.200"
          borderRadius="6px"
          textAlign="left"
          _hover={{ backgroundColor: 'warmNeutral.0' }}
          _active={{ backgroundColor: 'warmNeutral.0' }}
        >
          {t(selectedLanguage.key)}
        </MenuButton>

        <MenuList
          border="1px solid"
          borderColor="warmNeutral.200"
          borderRadius="6px"
          boxShadow="0px 4px 12px 0px rgba(0, 0, 0, 0.15)"
          paddingX={4}
          maxHeight="200px"
          overflowY="auto"
        >
          {companySupportedLanguages.map((lang, index) => (
            <MenuItem
              key={lang.value}
              value={lang.value}
              color="baseBlack"
              fontSize="14px"
              fontWeight={500}
              borderBottom={index !== companySupportedLanguages.length - 1 ? '1px solid' : 'none'}
              borderBottomColor="neutral.100"
              paddingY={4}
              _hover={{ backgroundColor: 'warmNeutral.0' }}
              onClick={() => handleLanguageChange(lang.value)}
            >
              {t(lang.key)}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Stack>
  );
}
